import React, { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import axios from "axios";
import { BedDouble, Link2Off, MapPin } from "lucide-react";
import { API, errMsg } from "@/lib/api";
import { inr } from "@/lib/format";
import { Card, ListSkeleton } from "@/components/common";
import { Wordmark } from "@/components/Brand";
import { RoomAmenityChips } from "@/components/RoomAmenities";
import { RoomCompareTable } from "@/components/RoomCompare";

function PGGroup({ g }: { g: any }) {
  return (
    <Card className="p-4" data-testid={`shared-pg-${g.id}`}>
      <p className="font-bold text-lg">{g.name}</p>
      {g.area && <p className="text-xs text-slate-500 flex items-center gap-1 mb-1"><MapPin className="h-3 w-3" />{g.area}</p>}
      {g.rooms.map((r) => (
        <div key={r.id} className="py-3 border-t border-slate-100 first:border-0" data-testid={`shared-room-${r.id}`}>
          <div className="flex justify-between gap-3">
            <div className="min-w-0">
              <p className="font-semibold">Room {r.name}{r.floor ? <span className="text-xs text-slate-500"> · Floor {r.floor}</span> : null}</p>
              <p className="text-xs text-slate-500">{r.sharing === 1 ? "Single room" : `${r.sharing}-sharing`} · Deposit {inr(r.deposit)}</p>
              <p className={`mt-1 text-xs font-semibold flex items-center gap-1 ${r.vacant_beds ? "text-green-700" : "text-slate-400"}`}><BedDouble className="h-3.5 w-3.5" />{r.vacant_beds ? `${r.vacant_beds} of ${r.sharing} beds free` : "Full"}</p>
            </div>
            <p className="text-lg font-extrabold font-mono-num text-[#131e2e] shrink-0">{inr(r.rent_per_bed)}</p>
          </div>
          <div className="mt-2"><RoomAmenityChips items={r.amenities} testId={`shared-room-amenities-${r.id}`} /></div>
        </div>
      ))}
    </Card>
  );
}

const group = (rooms: any[]) => Object.values(rooms.reduce((m: any, r) => {
  (m[r.property_id] ||= { id: r.property_id, name: r.pg_name, area: r.pg_area, rooms: [] }).rooms.push(r);
  return m;
}, {}));

export default function PublicShortlist() {
  const { token } = useParams();
  const [d, setD] = useState<any>(null);
  const [err, setErr] = useState<string | null>(null);
  useEffect(() => { axios.get(`${API}/public/shortlist/${token}`).then((r) => setD(r.data)).catch((e) => setErr(errMsg(e))); }, [token]);
  return (
    <div className="min-h-screen bg-[#f7f9fc]">
      <header className="h-16 border-b border-[#e3e8ef] bg-white"><div className="max-w-2xl mx-auto h-full px-4 flex items-center"><Wordmark size={26} /></div></header>
      <main className="max-w-2xl mx-auto px-4 py-6 space-y-4">
        {err ? (
          <Card className="p-6 text-center" data-testid="shared-error"><Link2Off className="h-8 w-8 mx-auto text-slate-400 mb-2" /><p className="font-semibold">{err}</p></Card>
        ) : !d ? <ListSkeleton rows={3} /> : (
          <>
            <div className="pg-rise">
              <p className="text-xs font-bold uppercase tracking-wider text-[#3d52a0]">Shared shortlist</p>
              <h1 data-testid="shared-title" className="text-3xl font-extrabold tracking-tight mt-1">{d.name ? `${d.name}'s saved rooms` : "Saved rooms"}</h1>
              <p className="text-sm text-slate-500 mt-1">{d.rooms.length} room{d.rooms.length === 1 ? "" : "s"} shortlisted on Nexstays. Help pick the best one!</p>
            </div>
            {!d.rooms.length ? <Card className="p-6 text-center text-sm text-slate-500" data-testid="shared-empty">No rooms in this shortlist right now.</Card> : (
              <>
                {d.rooms.length > 1 && <Card className="p-4"><p className="font-bold mb-2">Side by side</p><RoomCompareTable rooms={d.rooms} /></Card>}
                <div className="space-y-3">{group(d.rooms).map((g: any) => <PGGroup key={g.id} g={g} />)}</div>
              </>
            )}
            <p className="text-center text-sm text-slate-500 pt-2">Looking for a PG too? <Link to="/resident" data-testid="shared-cta" className="font-semibold text-[#131e2e] underline">Try Nexstays</Link></p>
          </>
        )}
      </main>
    </div>
  );
}
